/**
 * Yönetici merkezi (V5-12).
 *
 * Normal kullanıcı hesabından ayrı bir rota: metadata gelen kutusu ve aktif
 * oturumlar. Düşük güvenli metadata toplu onaya kapalıdır; oturum kapatma
 * yıkıcı olduğu için açık onay ister.
 */
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import {
  type AdminMetadataItem,
  type AdminSession,
  type AdminSystem,
  type AdminUser,
  api,
} from '../api.ts';
import { AdminSystemPanel } from '../components/AdminSystemPanel.tsx';
import { AdminUsersPanel } from '../components/AdminUsersPanel.tsx';
import { ErrorNotice, Loading } from '../components/States.tsx';
import {
  AlertIcon,
  CheckIcon,
  RefreshIcon,
  ShieldIcon,
  SparklesIcon,
  TrashIcon,
} from '../Icon.tsx';
import { useSession } from '../session.tsx';

type Tab = 'metadata' | 'users' | 'sessions' | 'system';
type Busy = string | null;

const REASON_LABEL: Record<AdminMetadataItem['reason'], string> = {
  missing: 'Eşleşme yok',
  pending: 'Onay bekliyor',
  low_confidence: 'Düşük güven',
  missing_image: 'Görsel eksik',
};

function reasonClass(reason: AdminMetadataItem['reason']): string {
  return `admin-badge admin-badge--${reason}`;
}

function formatWhen(ms: number | null): string {
  if (ms === null) return '—';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(ms));
}

export function AdminScreen(): ReactNode {
  const { user } = useSession();
  const [tab, setTab] = useState<Tab>('metadata');
  const [items, setItems] = useState<AdminMetadataItem[]>([]);
  const [threshold, setThreshold] = useState(0.72);
  const [metadataEnabled, setMetadataEnabled] = useState(true);
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [system, setSystem] = useState<AdminSystem | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inbox, sessionResult, userResult, systemResult] = await Promise.all([
        api.adminMetadataInbox(),
        api.adminSessions(),
        api.adminUsers(),
        api.adminSystem(),
      ]);
      setItems(inbox.items);
      setThreshold(inbox.threshold);
      setMetadataEnabled(inbox.enabled);
      setSessions(sessionResult.sessions);
      setAdminUsers(userResult.users);
      setSystem(systemResult);
      setSelected(new Set());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Yönetim verileri okunamadı.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // `reloadKey` yalnız aynı yüklemeyi yeniden çalıştırmak için.
    void reloadKey;
    void load();
  }, [load, reloadKey]);

  if (!user) return <Loading label="Yönetici oturumu okunuyor…" />;
  if (user.role !== 'admin') {
    return <ErrorNotice message="Yönetim merkezi yalnız yöneticilere açıktır." />;
  }

  const selectedItems = items.filter((item) => selected.has(item.itemId));
  const selectedApprovable = selectedItems.filter(
    (item) => item.reason === 'pending' && item.confidence !== null && item.confidence >= threshold,
  );

  const toggle = (itemId: string): void => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const approve = async (ids: string[]): Promise<void> => {
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `${ids.length} yüksek güvenli eşleşme onaylanacak. Düşük güvenli kayıtlar onaylanmayacak. Devam edilsin mi?`,
      )
    ) {
      return;
    }
    setBusy('approve');
    setNotice(null);
    try {
      const result = await api.adminApproveMetadata(ids);
      setNotice(
        `${result.approved.length} kayıt onaylandı; ${result.skipped.length} kayıt atlandı.`,
      );
      setReloadKey((value) => value + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Eşleşmeler onaylanamadı.');
    } finally {
      setBusy(null);
    }
  };

  const refresh = async (ids: string[]): Promise<void> => {
    if (ids.length === 0) return;
    setBusy('refresh');
    setNotice(null);
    try {
      const result = await api.adminRefreshMetadata(ids);
      const ok = result.results.filter((entry) => entry.status !== 'error').length;
      setNotice(`${ok} kayıt için metadata yenilendi.`);
      setReloadKey((value) => value + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Metadata yenilenemedi.');
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (session: AdminSession): Promise<void> => {
    if (session.current) return;
    if (!window.confirm(`${session.deviceName} oturumu kapatılsın mı?`)) return;
    setBusy(`session:${session.id}`);
    setNotice(null);
    try {
      await api.adminRevokeSession(session.id);
      setNotice('Oturum kapatıldı.');
      setReloadKey((value) => value + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Oturum kapatılamadı.');
    } finally {
      setBusy(null);
    }
  };

  const revokeOthers = async (): Promise<void> => {
    if (!window.confirm('Açık oturumunuz dışındaki tüm oturumlarınız kapatılsın mı?')) return;
    setBusy('revoke-others');
    setNotice(null);
    try {
      const result = await api.adminRevokeOtherSessions();
      setNotice(`${result.revoked} oturum kapatıldı.`);
      setReloadKey((value) => value + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Oturumlar kapatılamadı.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <Loading label="Yönetim merkezi yükleniyor…" />;
  if (error !== null) {
    return <ErrorNotice message={error} onRetry={() => setReloadKey((value) => value + 1)} />;
  }

  return (
    <div className="admin">
      <header className="admin__header">
        <div>
          <p className="admin__eyebrow">
            <ShieldIcon size={14} /> Yönetici alanı
          </p>
          <h1 className="admin__title">Yönetim Merkezi</h1>
          <p className="admin__sub">
            Metadata gelen kutusu ve açık oturumlar; normal hesap ayarlarından ayrıdır.
          </p>
        </div>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setReloadKey((value) => value + 1)}
          disabled={busy !== null}
        >
          <RefreshIcon size={15} />
          Yenile
        </button>
      </header>

      {notice !== null && (
        <p className="admin__notice" role="status">
          <CheckIcon size={15} /> {notice}
        </p>
      )}

      <div className="admin__tabs" role="tablist" aria-label="Yönetim bölümleri">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'metadata'}
          className="admin__tab"
          onClick={() => setTab('metadata')}
        >
          Metadata <span className="admin__count">{items.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'users'}
          className="admin__tab"
          onClick={() => setTab('users')}
        >
          Kullanıcılar <span className="admin__count">{adminUsers.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'sessions'}
          className="admin__tab"
          onClick={() => setTab('sessions')}
        >
          Oturumlar <span className="admin__count">{sessions.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'system'}
          className="admin__tab"
          onClick={() => setTab('system')}
        >
          Sistem
        </button>
      </div>

      {tab === 'metadata' ? (
        <section className="admin__panel" aria-label="Metadata gelen kutusu">
          {!metadataEnabled && (
            <div className="admin__warning" role="status">
              <AlertIcon size={16} />
              Metadata sağlayıcısı yapılandırılmadı. Mevcut kayıtlar görüntülenebilir; yenileme
              kapalı.
            </div>
          )}

          {items.length === 0 ? (
            <div className="admin__empty">
              <SparklesIcon size={28} />
              <p>Onay bekleyen veya eksik metadata kaydı yok.</p>
            </div>
          ) : (
            <>
              <div className="admin__bulk">
                <span>{selected.size} seçili</span>
                <button
                  type="button"
                  className="button button--primary"
                  disabled={selectedApprovable.length === 0 || busy !== null || !metadataEnabled}
                  onClick={() => void approve(selectedApprovable.map((item) => item.itemId))}
                >
                  <CheckIcon size={15} />
                  Yüksek güvenli {selectedApprovable.length} kaydı onayla
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={selected.size === 0 || busy !== null || !metadataEnabled}
                  onClick={() => void refresh(selectedItems.map((item) => item.itemId))}
                >
                  <RefreshIcon size={15} />
                  Seçilenleri yenile
                </button>
                <span className="admin__threshold">
                  Toplu onay eşiği: %{Math.round(threshold * 100)}
                </span>
              </div>

              <ul className="admin__list">
                {items.map((item) => {
                  const canApprove =
                    item.reason === 'pending' &&
                    item.confidence !== null &&
                    item.confidence >= threshold;
                  return (
                    <li key={item.itemId} className="admin__item">
                      <label className="admin__select">
                        <input
                          type="checkbox"
                          checked={selected.has(item.itemId)}
                          onChange={() => toggle(item.itemId)}
                          aria-label={`${item.title} seç`}
                        />
                      </label>
                      <div className="admin__item-main">
                        <div className="admin__item-title-row">
                          <strong>{item.title}</strong>
                          {item.year !== null && <span>{item.year}</span>}
                          <span className={reasonClass(item.reason)}>
                            {REASON_LABEL[item.reason]}
                          </span>
                        </div>
                        <p className="admin__item-meta">
                          {item.libraryName} · {item.kind === 'show' ? 'Dizi' : 'Film'}
                          {item.confidence !== null
                            ? ` · güven %${Math.round(item.confidence * 100)}`
                            : ''}
                          {item.metadataTitle !== null ? ` · öneri: ${item.metadataTitle}` : ''}
                        </p>
                      </div>
                      <div className="admin__item-actions">
                        <a className="button button--ghost" href={`#/oge/${item.itemId}`}>
                          İncele
                        </a>
                        {canApprove && (
                          <button
                            type="button"
                            className="button button--primary"
                            disabled={busy !== null}
                            onClick={() => void approve([item.itemId])}
                          >
                            Onayla
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      ) : tab === 'users' ? (
        <AdminUsersPanel
          users={adminUsers}
          currentUserId={user.id}
          onChanged={() => setReloadKey((value) => value + 1)}
        />
      ) : tab === 'system' ? (
        <AdminSystemPanel system={system} />
      ) : (
        <section className="admin__panel" aria-label="Açık oturumlar">
          <div className="admin__bulk">
            <span>{sessions.length} açık oturum</span>
            <button
              type="button"
              className="button button--ghost admin__danger"
              disabled={busy !== null || sessions.length <= 1}
              onClick={() => void revokeOthers()}
            >
              <TrashIcon size={15} />
              Diğer oturumlarımı kapat
            </button>
          </div>

          <ul className="admin__list">
            {sessions.map((session) => (
              <li key={session.id} className="admin__session">
                <div className="admin__session-device">
                  <strong>{session.deviceName}</strong>
                  {session.current && <span className="admin__badge">Bu oturum</span>}
                </div>
                <div className="admin__session-meta">
                  <span>
                    {session.displayName} · @{session.username}
                  </span>
                  <span>Son etkinlik: {formatWhen(session.lastUsedAt ?? session.createdAt)}</span>
                  <span>Bitiş: {formatWhen(session.expiresAt)}</span>
                </div>
                <button
                  type="button"
                  className="button button--ghost admin__danger"
                  disabled={session.current || busy !== null}
                  onClick={() => void revoke(session)}
                >
                  Kapat
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
